import _ from "lodash";
import moment from "moment-timezone";
import React from "react";

import Button from "@/components/UI/Button";
import { scheduleRequests } from "@/api/(requests)/schedule";
import { workflowRequests } from "@/api/(requests)/workflow";

import WorkflowHeader from "./WorkflowHeader";

const RescheduleButtonOrActionText = ({
  addToInAppHistory,
  alreadyScheduled,
  appToDisplay,
  consultationToConfirm,
  setAlreadyScheduled,
  setAppToDisplay,
  setConsultationToConfirm,
  setWithinAppHistory,
  withinAppHistory,
}) => {
  if (!alreadyScheduled) {
    return (
      <div className="txt-gry fs-14">
        You will receive an email and text confirming your session once scheduled.
      </div>
    );
  }
  if (alreadyScheduled) {
    return (
      <Button
        className="Btn Btn--sec Btn--sm-2 workflow-btn"
        onClick={() => {
          addToInAppHistory(
            appToDisplay,
            setAppToDisplay,
            alreadyScheduled,
            setAlreadyScheduled,
            consultationToConfirm,
            setConsultationToConfirm,
            withinAppHistory,
            setWithinAppHistory,
          );
          setAppToDisplay("scheduleConsultation");
          // setAlreadyScheduled(false);
        }}
        type="button"
      >
        Reschedule
      </Button>
    );
  }
};

const confirmScheduling = async (
  workflowInstance,
  consultationToConfirm,
  alreadyScheduled,
  schedulingData,
) => {
  // schedule call if not already scheduled
  if (!alreadyScheduled) {
    await scheduleCall(consultationToConfirm, workflowInstance);
  } else {
    // Already scheduled and now rescheduling since slot without a call_id picked
    if (consultationToConfirm["call_id"] == null) {
      const currentCallId = _.get(
        schedulingData,
        "intake_appointment_data.upcoming_appointments[0].call_id",
      );

      await rescheduleCall(currentCallId, consultationToConfirm, workflowInstance);
    }
  }

  const data = {
    node_name: "schedule_appointment",
    state: {
      appointment_confirmed: true,
    },
    workflow_instance_sort_key: workflowInstance.sort_key,
  };
  const headers = {
    "X-On-Behalf-Of": workflowInstance.entity_id,
  };

  return workflowRequests.update_state_and_get_next_node({ data, headers });
};

const callDetailsToDateData = (callDetails) => {
  let timezone = moment.tz.guess();
  let combinedDateTimeStr = `${callDetails.year}-${callDetails.month}-${callDetails.day} ${callDetails.start_time}`;
  let utcDatetime = moment.tz(combinedDateTimeStr, "YYYY-MM-DD HH:mm", timezone).utc();

  return {
    date: utcDatetime.format("YYYY-MM-DD"),
    time: utcDatetime.format("HH:mm"),
    timezone,
  };
};

const scheduleCall = async (callDetails, workflowInstance) => {
  const data = {
    allotted_time: callDetails["allotted_time"],
    local_date_time: `${callDetails.year}-${callDetails.month}-${callDetails.day} ${callDetails.start_time}`,
    patient_id: workflowInstance.entity_id,
    provider_id: callDetails["provider_username"],
    timezone: moment.tz.guess(),
  };

  const headers = {
    // GROUP_AUTH_HEADER_NAME is injected by schedule.js — only on-behalf-of headers go here
    "X-On-Behalf-Of": workflowInstance.entity_id,
    "X-On-Behalf-Of-Entity-Type": "patient",
  };

  return scheduleRequests.post_schedule({ data, headers });
};

const rescheduleCall = async (currentCallId, callDetails, workflowInstance) => {
  let dateTimeData = callDetailsToDateData(callDetails);

  const data = {
    allotted_time: callDetails["allotted_time"],
    call_id: currentCallId,
    local_date_time: `${callDetails.year}-${callDetails.month}-${callDetails.day} ${callDetails.start_time}`,
    patient_id: workflowInstance.entity_id,
    provider_id: callDetails["provider_username"],
    timezone: dateTimeData["timezone"],
  };

  const headers = {
    // GROUP_AUTH_HEADER_NAME is injected by schedule.js — only on-behalf-of headers go here
    "X-On-Behalf-Of": workflowInstance.entity_id,
    "X-On-Behalf-Of-Entity-Type": "patient",
  };

  return scheduleRequests.put_schedule({ data, headers });
};

const ContextSpecificButtons = ({
  alreadyScheduled,
  consultationToConfirm,
  goBackInAppHistory,
  schedulingData,
  setAppToDisplay,
  setWithinAppHistory,
  withinAppHistory,
  workflowInstance,
}) => {
  return (
    <div className="workflow-container-sm d-flex justify-content-between">
      <Button
        className="Btn Btn--otl-gry Btn--sm-2 workflow-btn"
        onClick={() => {
          goBackInAppHistory(withinAppHistory, setWithinAppHistory);
        }}
        type="button"
      >
        Back
      </Button>

      <Button
        className="Btn Btn--pri Btn--sm-2 workflow-btn"
        onClick={async () => {
          await confirmScheduling(
            workflowInstance,
            consultationToConfirm,
            alreadyScheduled,
            schedulingData,
          );
          setAppToDisplay("thankYou");
        }}
        type="button"
      >
        Confirm & Schedule
      </Button>
    </div>
  );
};

const ConfirmIntakeConsultation = ({
  addToInAppHistory,
  alreadyScheduled,
  appToDisplay,
  consultationToConfirm,
  goBackInAppHistory,
  schedulingData,
  setAlreadyScheduled,
  setAppToDisplay,
  setConsultationToConfirm,
  setWithinAppHistory,
  withinAppHistory,
  workflowInstance,
}) => {
  const startTime = moment(consultationToConfirm.start_time, "HH:mm").format("h:mm");
  const endTime = moment(consultationToConfirm.end_time, "HH:mm").format("h:mm a");

  return (
    <>
      <WorkflowHeader header="Confirm your consultation" showImage={true} />
      <div className="workflow-container-sm workflow-container-border mb-5">
        <div className="p-lg-5 p-3">
          <div className="row">
            <div className="txt-gry fs-14">Date</div>
          </div>

          <div className="row">
            <div className="fs-md-16 fs-14 tw:font-bold">{`${consultationToConfirm.day_of_week}, ${consultationToConfirm.month_of_year} ${consultationToConfirm.day}, ${consultationToConfirm.year}`}</div>
          </div>

          <br />

          <div className="row">
            <div className="txt-gry fs-14">Time</div>
          </div>

          <div className="row">
            <div className="fs-md-16 fs-14 tw:font-bold">{`${startTime} - ${endTime}`}</div>
          </div>

          <br />

          <div className="row">
            <div className="txt-gry fs-14">Intake Coordinator</div>
          </div>

          <div className="row">
            <div className="fs-md-16 fs-14 tw:font-bold">{consultationToConfirm.provider_name}</div>
          </div>

          <br />

          <div className="row">
            <RescheduleButtonOrActionText
              addToInAppHistory={addToInAppHistory}
              alreadyScheduled={alreadyScheduled}
              appToDisplay={appToDisplay}
              consultationToConfirm={consultationToConfirm}
              schedulingData={schedulingData}
              setAlreadyScheduled={setAlreadyScheduled}
              setAppToDisplay={setAppToDisplay}
              setConsultationToConfirm={setConsultationToConfirm}
              setWithinAppHistory={setWithinAppHistory}
              withinAppHistory={withinAppHistory}
            />
          </div>
        </div>
      </div>

      <ContextSpecificButtons
        addToInAppHistory={addToInAppHistory}
        alreadyScheduled={alreadyScheduled}
        appToDisplay={appToDisplay}
        consultationToConfirm={consultationToConfirm}
        goBackInAppHistory={goBackInAppHistory}
        schedulingData={schedulingData}
        setAlreadyScheduled={setAlreadyScheduled}
        setAppToDisplay={setAppToDisplay}
        setConsultationToConfirm={setConsultationToConfirm}
        setWithinAppHistory={setWithinAppHistory}
        withinAppHistory={withinAppHistory}
        workflowInstance={workflowInstance}
      />
    </>
  );
};

export default ConfirmIntakeConsultation;
